//core
import React, {Dispatch, SetStateAction, useContext, useEffect, useState} from "react";
import { useTranslation } from "react-i18next";
import format from "date-fns/format";
import { endOfDay, startOfDay } from "date-fns";
import { useSelector } from "react-redux";
import { useLocalStorage } from "usehooks-ts";
import {
  Box,
  SelectChangeEvent,
  useMediaQuery,
} from "@mui/material";

//styles
import { useStyles } from "./styles";

//helpers
import {getDataIds, getGroupByOptions, getPeriod, PeriodTypes} from "../../../../../utils";

//constant
import { fullPeriodOptions } from "../../../../../constant";
import { favoriteFilterKeys } from "../../../../constants";

//selectors
import {
  selectDealsData,
  selectOptionsData, selectPlatformsData
} from "../../../../../../../../store/admin/reports/sub_affiliate_report_v2/selectors";

//hooks
import { useRegistration } from "../../../../../../../../store/common/registration/useRegistration";
import { useSubAffiliateReportV2 } from "../../../../../../../../store/admin/reports/sub_affiliate_report_v2/useSubAffiliateReportV2";

//context
import { GlobalContext } from "../../../../../../../../context/GlobalContext";

//components
import CSelect from "../../../../../../../UI/Fields/CSelect";
import CDateRangePicker from "../../../../../../../UI/Fields/CDateRangePicker";
import GroupByField, { IItemOption } from "../../../GroupByField";
import MuiAutocompleteSelectLazy from "../../../../../../../UI/Fields/MuiAutocompleteSelectLazy";
import { ISubAffiliateReportV2FilterDataType } from "../../../../types";
import MuiAutocompleteSelect from "../../../../../../../UI/Fields/MuiAutocompleteSelect";
import MuiChipsInputField from "../../../../../../../UI/Fields/MuiChipsInput";


interface IVisibleFilterProps {
  filter: ISubAffiliateReportV2FilterDataType;
  setUpdateFilter: Dispatch<SetStateAction<ISubAffiliateReportV2FilterDataType>>;
  apiV2: boolean;
}

const VisibleFilter: React.FC<IVisibleFilterProps> = ({
  filter,
  setUpdateFilter,
  apiV2 = false,
}): JSX.Element => {
  const { t, i18n } = useTranslation();
  const styles = useStyles();
  const mobile = useMediaQuery('(max-width:767px)');

  const { getFiltersField } = useSubAffiliateReportV2();
	const { countries } = useRegistration();

	const context = useContext(GlobalContext);

  const optionsData = useSelector(selectOptionsData);

  //state
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodTypes>(filter.period);
  const [startDate, setStartDate] = useState<Date | ''>(getPeriod(filter.period).start);
  const [endDate, setEndDate] = useState<Date | ''>(getPeriod(filter.period).end);

	const [dataCountries, setDataCountries] = useState<any | null>(null);

  const [groupBy, setGroupBy] = useState<IItemOption[]>(filter.group_by.fields);

  //favorite fields state
	const [favAdmSARV2WhoEarnedIds, setFavAdmSARV2WhoEarnedIds] = useLocalStorage<boolean>(favoriteFilterKeys.whoEarnedIds, false);
	const [favAdmSARV2ReferredAffiliateIds, setFavAdmSARV2ReferredAffiliateIds] = useLocalStorage<boolean>(favoriteFilterKeys.referredAffiliateIds, false);
	const [favAdmSARV2CommissionLevels, setFavAdmSARV2CommissionLevels] = useLocalStorage<boolean>(favoriteFilterKeys.commissionLevels, false);
	const [favAdmSARV2Deals, setFavAdmSARV2Deals] = useLocalStorage<boolean>(favoriteFilterKeys.deals, false);
	const [favAdmSARV2PlayerIds, setFavAdmSARV2PlayerIds] = useLocalStorage<boolean>(favoriteFilterKeys.playerIds, false);
	const [favAdmSARV2Brands, setFavAdmSARV2Brands] = useLocalStorage<boolean>(favoriteFilterKeys.brands, false);
	const [favAdmSARV2Currencies, setFavAdmSARV2Currencies] = useLocalStorage<boolean>(favoriteFilterKeys.currencies, false);
	const [favAdmSARV2PlatformIds, setFavAdmSARV2PlatformIds] = useLocalStorage<boolean>(favoriteFilterKeys.platformIds, false);
	const [favAdmSARV2ProductIds, setFavAdmSARV2ProductIds] = useLocalStorage<boolean>(favoriteFilterKeys.productIds, false);
	const [favAdmSARV2Geo, setFavAdmSARV2Geo] = useLocalStorage<boolean>(favoriteFilterKeys.geo, false);

  useEffect(() => {
    if (!!selectedPeriod) {
      const period = getPeriod(selectedPeriod);

      if (!!period.start && !!period.end) {
        setStartDate(period.start);
        setEndDate(period.end);

        context.setSubAffiliateReportV2Filter({
          ...context.subAffiliateReportV2Filter,
          period: selectedPeriod,
          sent_from: format(startOfDay(new Date(period.start)), "yyyy-MM-dd HH:mm:ss"),
          sent_to: format(endOfDay(new Date(period.end)), "yyyy-MM-dd HH:mm:ss"),
        });
        setUpdateFilter((prevState: any) => {
          return {
            ...prevState,
            period: selectedPeriod,
            sent_from: format(startOfDay(new Date(period.start)), "yyyy-MM-dd HH:mm:ss"),
            sent_to: format(endOfDay(new Date(period.end)), "yyyy-MM-dd HH:mm:ss"),
          }
        })
      }
    }
  }, [selectedPeriod]);

  useEffect(() => {
    setSelectedPeriod(filter.period);
  }, [filter.period]);

  useEffect(() => {
    setGroupBy(filter.group_by.fields);
  }, [filter.group_by.fields]);

  useEffect(() => {
    setUpdateFilter((prevState) => {
      return {
        ...prevState,
        group_by: {
          fields: groupBy,
        },
      }
    })
  }, [groupBy]);

	useEffect(() => {
		if (!!countries.data) {
			const data = countries.data.reduce((acc: any[], item: any) => {
				acc.push({
					id: item.code,
					value: item.code,
				});
				return acc;
			}, []);
			setDataCountries({
				list: data,
				total_entries: data.length,
			});
		}
	}, [countries, i18n.language]);


	useEffect(() => {
    context.setSubAffiliateReportV2Filter(filter);
  }, [filter]);


  const handleChangePeriodSelect = (event: SelectChangeEvent) => {
    //@ts-ignore
    const value: PeriodTypes = event.target.value;
    setSelectedPeriod(value);
  };

  const handleChangeDates = (start: Date, end: Date) => {
    const noEndDate = !end;

    setStartDate(start);
    setEndDate(noEndDate ? start : end);
    context.setSubAffiliateReportV2Filter({
      ...context.subAffiliateReportV2Filter,
      period: '',
      sent_from: format(startOfDay(new Date(start)), "yyyy-MM-dd HH:mm:ss"),
      sent_to: format(endOfDay(new Date(noEndDate ? start : end)), "yyyy-MM-dd HH:mm:ss"),
    });
    setUpdateFilter((prevState: any) => {
      return {
        ...prevState,
        period: '',
        sent_from: format(startOfDay(new Date(start)), "yyyy-MM-dd HH:mm:ss"),
        sent_to: format(endOfDay(new Date(noEndDate ? start : end)), "yyyy-MM-dd HH:mm:ss"),
      }
    });
  };

  const fieldPayload = () => {
    return {
      page: 1,
      search: null,
      perPage: 100,
    }
  };

  const fieldCurrenciesPayload = () => {
    return {
      page: 1,
      search: null,
      perPage: 100,
      brandCodes: !!filter.brands.length ? getDataIds(filter.brands) : null,
    }
  };

  const favoriteFields = !!dataCountries && !!optionsData && !mobile &&
  (favAdmSARV2WhoEarnedIds || favAdmSARV2ReferredAffiliateIds || favAdmSARV2CommissionLevels ||
	  favAdmSARV2Deals || favAdmSARV2PlayerIds || favAdmSARV2Brands || favAdmSARV2Currencies ||
	  favAdmSARV2PlatformIds || favAdmSARV2ProductIds || favAdmSARV2Geo)
    ? (
      <Box className={styles.favoriteFields}>
	      {/*whoEarnedIds*/}
	      {optionsData.availableFilters.includes("who-earned") && (<>
		      {favAdmSARV2WhoEarnedIds && (
			      <Box>
				      <MuiAutocompleteSelectLazy
					      keyValue="value"
					      keyId="id"
					      id="whoEarnedIds"
					      name="whoEarnedIds"
					      label={t(`admin.reports.filter.whoEarnedIds`)}
					      value={filter.whoEarnedIds}
					      multiple={true}
					      showSelectAll={false}
					      disabled={false}
					      getOptions={getFiltersField}
					      payloadData={fieldPayload()}
					      labelWithId={true}
					      onChange={setUpdateFilter}
					      disableCloseOnSelect={true}
					      showLabelCounter={true}
					      showNameInPayload={true}
				      />
			      </Box>
		      )}
	      </>)}
	      {/*referredAffiliateIds*/}
	      {optionsData.availableFilters.includes("referred-affiliates") && (<>
		      {favAdmSARV2ReferredAffiliateIds && (
			      <Box>
				      <MuiAutocompleteSelectLazy
					      keyValue="value"
					      keyId="id"
					      id="referredAffiliateIds"
					      name="referredAffiliateIds"
					      label={t(`admin.reports.filter.referredAffiliateIds`)}
					      value={filter.referredAffiliateIds}
					      multiple={true}
					      showSelectAll={false}
					      disabled={false}
					      getOptions={getFiltersField}
					      payloadData={fieldPayload()}
					      labelWithId={true}
					      onChange={setUpdateFilter}
					      disableCloseOnSelect={true}
					      showLabelCounter={true}
					      showNameInPayload={true}
				      />
			      </Box>
		      )}
	      </>)}
	      {/*commissionLevels*/}
	      {optionsData.availableFilters.includes("commission-levels") && (<>
		      {favAdmSARV2CommissionLevels && (
			      <Box>
				      <MuiAutocompleteSelectLazy
					      keyValue="value"
					      keyId="id"
					      id="commissionLevels"
					      name="commissionLevels"
					      label={t(`admin.reports.filter.commissionLevels`)}
					      value={filter.commissionLevels}
					      multiple={true}
					      showSelectAll={false}
					      disabled={false}
					      getOptions={getFiltersField}
					      payloadData={fieldPayload()}
					      labelWithId={false}
					      onChange={setUpdateFilter}
					      disableCloseOnSelect={true}
					      showLabelCounter={true}
					      showNameInPayload={true}
				      />
			      </Box>
		      )}
	      </>)}
	      {/*deals*/}
	      {optionsData.availableFilters.includes("deals") && (<>
		      {favAdmSARV2Deals && (
			      <Box>
				      <MuiAutocompleteSelectLazy
					      keyValue="value"
					      keyId="id"
					      id="deals"
					      name="deals"
					      label={t(`admin.reports.filter.deals`)}
					      value={filter.deals}
					      multiple={true}
					      showSelectAll={false}
					      disabled={false}
					      getOptions={getFiltersField}
					      payloadData={fieldPayload()}
					      labelWithId={true}
					      onChange={setUpdateFilter}
					      disableCloseOnSelect={true}
					      showLabelCounter={true}
					      showNameInPayload={true}
				      />
			      </Box>
		      )}
	      </>)}
	      {/*players*/}
	      {optionsData.availableFilters.includes("players") && (<>
		      {favAdmSARV2PlayerIds && (
			      <Box>
				      <MuiChipsInputField
					      id="players"
					      name="players"
					      label={t(`admin.reports.filter.players`)}
					      value={filter.players}
					      onChange={setUpdateFilter}
				      />
			      </Box>
		      )}
	      </>)}
	      {/*brands*/}
	      {optionsData.availableFilters.includes("brands") && (<>
		      {favAdmSARV2Brands && (
			      <Box>
				      <MuiAutocompleteSelectLazy
					      keyValue="value"
					      keyId="id"
					      id="brands"
					      name="brands"
					      label={t(`admin.reports.filter.brands`)}
					      value={filter.brands}
					      multiple={true}
					      showSelectAll={false}
					      disabled={false}
					      getOptions={getFiltersField}
					      payloadData={fieldPayload()}
					      labelWithId={true}
					      onChange={setUpdateFilter}
					      disableCloseOnSelect={true}
					      showLabelCounter={true}
					      showNameInPayload={true}
				      />
			      </Box>
		      )}
	      </>)}
	      {/*currencies*/}
	      {optionsData.availableFilters.includes("currencies") && (<>
		      {favAdmSARV2Currencies && (
			      <Box>
				      <MuiAutocompleteSelectLazy
					      keyValue="value"
					      keyId="id"
					      id="currencies"
					      name="currencies"
					      label={t(`admin.reports.filter.currencies`)}
					      value={filter.currencies}
					      multiple={true}
					      showSelectAll={false}
					      disabled={false}
					      getOptions={getFiltersField}
					      payloadData={fieldCurrenciesPayload()}
					      labelWithId={false}
					      onChange={setUpdateFilter}
					      disableCloseOnSelect={true}
					      showLabelCounter={true}
					      showNameInPayload={true}
				      />
			      </Box>
		      )}
	      </>)}
	      {/*platformIds*/}
	      {optionsData.availableFilters.includes("platforms") && (<>
		      {favAdmSARV2PlatformIds && (
			      <Box>
				      <MuiAutocompleteSelectLazy
					      keyValue="value"
					      keyId="id"
					      id="platformIds"
					      name="platformIds"
					      label={t(`admin.reports.filter.platformIds`)}
					      value={filter.platformIds}
					      multiple={true}
					      showSelectAll={false}
					      disabled={false}
					      getOptions={getFiltersField}
					      payloadData={fieldPayload()}
					      labelWithId={false}
					      onChange={setUpdateFilter}
					      disableCloseOnSelect={true}
					      showLabelCounter={true}
					      showNameInPayload={true}
				      />
			      </Box>
		      )}
	      </>)}
	      {/*productIds*/}
	      {optionsData.availableFilters.includes("products") && (<>
		      {favAdmSARV2ProductIds && (
			      <Box>
				      <MuiAutocompleteSelectLazy
					      keyValue="value"
					      keyId="id"
					      id="productIds"
					      name="productIds"
					      label={t(`admin.reports.filter.productIds`)}
					      value={filter.productIds}
					      multiple={true}
					      showSelectAll={false}
					      disabled={false}
					      getOptions={getFiltersField}
					      payloadData={fieldPayload()}
					      labelWithId={false}
					      onChange={setUpdateFilter}
					      disableCloseOnSelect={true}
					      showLabelCounter={true}
					      showNameInPayload={true}
				      />
			      </Box>
		      )}
	      </>)}
	      {/*geo*/}
	      {optionsData.availableFilters.includes("geo") && (<>
		      {favAdmSARV2Geo && apiV2 && (
			      <MuiAutocompleteSelect
				      keyValue="value"
				      keyId="id"
				      id="geo"
				      name="geo"
				      label={t(`admin.reports.filter.geo`)}
				      value={filter.geo}
				      multiple={true}
				      showSelectAll={false}
				      disabled={false}
				      staticOptions={dataCountries.list}
				      onChange={setUpdateFilter}
				      disableCloseOnSelect={true}
				      maxHeightFieldScroll={32}
				      showLabelCounter={true}
				      prefix={`common.components.creative.list_geolocales.`}
			      />
		      )}
	      </>)}

      </Box>
    ) : null;

  return (<>
    {!!optionsData && (
      <Box>
        <Box className={styles.wrapper}>
          <Box className={styles.box}>
            <CSelect
              name="period"
              label={t("admin.reports.filter.period")}
              value={selectedPeriod}
              options={fullPeriodOptions}
              onChange={handleChangePeriodSelect}
              prefix="common.components.periods."
            />
          </Box>
          <Box className={styles.boxDate}>
            <CDateRangePicker
              startDate={startDate}
              endDate={endDate}
              onChange={handleChangeDates}
            />
          </Box>
        </Box>
        <Box className={styles.field}>
          <GroupByField
            name="groupBy"
            value={filter.group_by.fields}
            options={getGroupByOptions(optionsData.groupBy)}
            setFilter={setGroupBy}
            prefix={"admin.sub_affiliate_report_v2.filter.group_by_select."}
          />
        </Box>
        {favoriteFields}
      </Box>
    )}
  </>);
};

export default VisibleFilter;
